import { apiClient } from "@/api/client";
import { Options } from "@/components/ui/DropDown";
import useDashboardStore, {
  CampaignData,
  CampaignDetailResponse,
} from "@/pages/dashboard/store/dashboardStore";
import { Team } from "@/pages/teams/store/teamsStore";
import { AxiosError } from "axios";
import { create } from "zustand";

export interface LanguageResponse {
  data: Language[];
}

export interface Language {
  id: string;
  name: string;
}

export interface TeamsRespose {
  data: Array<Team>;
}

interface Objective {
  id: string;
  name: string;
  desc: string;
}

interface AdCategory {
  id: string;
  name: string;
}

interface ObjectiveResponse {
  success: boolean;
  data: Objective[];
}

interface AdCategoryResponse {
  success: boolean;
  data: AdCategory[];
}

// Updated Assets API interfaces
export interface AssetsPageData {
  flight_id: string;
  flight_number: number;
  flight_start_date: string;
  flight_end_date: string;
  languages: AssetsLanguage[];
}

export interface AssetsLanguage {
  language_id: string;
  language_name: string;
  objectives: AssetsObjective[];
}

export interface AssetsObjective {
  objective_id: string;
  objective_name: string;
  locations: AssetsLocation[];
}

export interface AssetsLocation {
  region: string;
  flight_rows: {
    [flightRowId: string]: AssetsFlightRow;
  };
}

export interface AssetsFlightRow {
  flight_row_id: string;
  flight_row_number: string;
  ad_categories: AssetsAdCategory[];
}

export interface AssetsAdCategory {
  flight_ad_categories_id: string;
  ad_category_id: string;
  ad_category_name: string;
}

export interface AssetsPageResponse {
  success: boolean;
  data: AssetsPageData[];
}

// New interfaces for parameter definitions
export interface ParameterField {
  id: string;
  key: string;
  label: string;
  type: string;
  placeholder: string;
  is_required: boolean;
  is_multiple: boolean;
  defaultValue: any;
  options: Array<{ title: string; value: string }> | null;
  extra_meta: any;
  group_name: string | null;
  sort_order: number;
  parameter_value_type: string | null;
  parameter_value: any;
}

export interface ParameterDefinition {
  id: string;
  key: string;
  label: string;
  sort_order: number;
  group_name: string | null;
  extra_meta: any;
  fields: ParameterField[];
}

export interface ParameterDefinitions {
  broadcast: ParameterDefinition[];
  digital: ParameterDefinition[];
  outdoor_digital: ParameterDefinition[];
  print: ParameterDefinition[];
  social: ParameterDefinition[];
}

export interface ParameterVariantField {
  parameter_field_id: string;
  key: string;
  label: string;
  type: string;
  options: any;
  value: any;
}

export interface ParameterVariant {
  publisher_id: string;
  ad_type_id: string;
  media_type: string;
  value_type: "user_defined" | "single_publisher" | "multi_publisher";
  fields: ParameterVariantField[];
}

export interface UIParameter {
  key: string;
  label: string;
  media_type: string;
  input_type: string;
  definition_id: string;
  ui_schema: {
    allow_multiple: boolean;
  };
  variants: ParameterVariant[];
}

export interface UIAdType {
  id: string;
  name: string;
  media_type_id: string;
  media_type: string;
  parameters: UIParameter[];
}

export interface UIParametersByAdType {
  [adCategoryId: string]: UIAdType[];
}

export interface AdCategoryDataItem {
  ad_category_id: string;
  ad_category_name: string;
  versions: number;
  publishers: Array<{
    id: string;
    name: string;
    type: "single" | "multi";
  }>;
  ad_types: Array<{
    id: string;
    name: string;
    publisher_id: string;
  }>;
}

export interface AdCategoryDataResponse {
  success: boolean;
  data: AdCategoryDataItem[];
  parameter_definitions: ParameterDefinitions;
  ui_parameters_by_ad_type: UIParametersByAdType;
}

// Assets Store Interface
interface AssetsStoreRequest {
  campaign_id: string;
  data: AssetsFlightData[];
}

interface AssetsFlightData {
  flight_id: string;
  flight_rows: AssetsStoreFlightRow[];
}

interface AssetsStoreFlightRow {
  flight_row_id: string;
  flight_ad_categories: AssetsFlightAdCategory[];
}

interface AssetsFlightAdCategory {
  flight_ad_categories_id: string;
  ad_rows: AssetsAdRow[];
}

interface AssetsAdRow {
  ad_row_id: string;
  versions?: number;
  ad_type_id?: string;
  ad_category_id?: string;
  publisher_id?: string;
  parameters: AssetsParameter[];
}

interface AssetsParameter {
  parameter_definition_id: string;
  parameter_key: string;
  parameter_value: string;
}

//

interface CampaignRequest {
  name: string;
  budget: number;
  project_code?: string;
  file_naming_convention: string;
  client_team_id: string;
  team_ids: string[];
  language_ids: string[];
}

interface FlightsUpdateRequest {
  flights: FlightUpdateRequest[];
}

interface FlightUpdateRequest {
  flight_number: number;
  start_date?: string;
  end_date?: string;
  rows: FlightRowUpdateRequest[];
}

interface FlightRowUpdateRequest {
  id?: string;
  region?: string;
  ad_category?: string[];
}

export interface FlightsRequest {
  flights: FlightRequest[];
}

export interface FlightRequest {
  campaign_id: string;
  flight_number: number;
  start_date?: string;
  end_date?: string;
  rows: FlightRowRequest[];
}

export interface FlightRowRequest {
  flight_row_number?: number;
  language_id?: string;
  objective_id?: string;
  region?: string;
  ad_category?: string[];
}

interface FlightResponse {
  success: boolean;
  data: FlightData;
}

interface FlightGetResponse {
  success: boolean;
  data: Array<FlightData>;
}

interface FlightData {
  id: string;
  campaign_id: string;
  created_by: string;
  flight_number: number;
  start_date: string;
  end_date: string;
  updated_at: string;
  created_at: string;
  rows: FlightRow[];
}

interface FlightRow {
  id: string;
  campaign_id: string;
  flight_id: string;
  created_by: string;
  language_id: string;
  objective_id: string;
  region: string;
  updated_at: string;
  created_at: string;
  ad_category: AdCategory[];
}

interface AdCategory {
  id: string;
  campaign_id: string;
  flight_row_id: string;
  ad_category_id: string;
  created_by: string;
  updated_at: string;
  created_at: string;
}

//Assets Value Interface
export interface ExistingAssetsValues {
  [adCategoryId: string]: {
    ad_types: {
      [adTypeId: string]: {
        publishers: {
          [publisherId: string]: {
            asset_meta: {
              assets_row_id: string;
              versions: number;
              flight_row_id: string;
              flight_ad_categories_id: string;
              ad_type_id: string;
              ad_category_id: string;
              publisher_id: string;
              parameter_value_pair: {
                ad_row_id: string;
                ad_type_id: string;
                parameters: Array<{
                  id: string;
                  key: string;
                  value: string;
                  definition_id: string;
                }>;
                publisher_id: string;
                ad_category_id: string;
              };
            };
            parameters: Array<{
              id: string;
              assets_row_id: string;
              parameter_definition_id: string;
              parameter_key: string;
              value: string;
              value_json: {
                key: string;
                value: string;
              };
              created_at: string;
            }>;
          };
        };
      };
    };
  };
}

// Campaign Summary Interfaces
export interface CampaignSummaryData {
  campaign: {
    id: string;
    name: string;
    status: string;
    progress_status: string;
    budget: number;
    created_at: string;
  };
  teams: Array<{
    id: string;
    name: string;
    team_type: string;
  }>;
  users: any[];
  flights: Array<{
    id: string;
    flight_number: number;
    start_date: string;
    end_date: string;
  }>;
  stats: {
    total_flights: number;
    total_flight_rows: number;
    total_assets: number;
    unique_languages: number;
    unique_publishers: number;
    unique_ad_types: number;
    total_teams: number;
    total_users: number;
  };
  assets_preview: Array<{
    language: string;
    objective: string;
    region: string;
    category: string;
    publisher: string;
    ad_type: string;
    due_date: string | null;
  }>;
}

export interface CampaignSummaryResponse {
  success: boolean;
  message: string;
  data: CampaignSummaryData;
}

export interface AssetsTableRow {
  id: string;
  language: string;
  objective: string;
  region: string;
  category: string;
  publisher: string;
  ad_type: string;
  due_date: string;
}

export interface AssetsTableResponse {
  data: AssetsTableRow[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: Array<{
      url: string | null;
      label: string;
      active: boolean;
    }>;
    path: string;
    per_page: [number, number];
    to: number;
    total: number;
    campaign_id: string;
    with: string[];
    current_flight_id: string;
    available_flights: Array<{
      id: string;
      period: string;
    }>;
    sort: {
      column: string;
      direction: string;
    };
  };
}

interface CampaignState {
  clientOptions: Array<Options>;
  teamOptions: Array<Options>;
  languageOptions: Array<Options>;
  objectiveOptions: Array<Objective>;
  adCategoryOptions: Array<AdCategory>;
  campaign: CampaignData | null;
  assetsPageData: AssetsPageData[];
  adCategoryData: AdCategoryDataItem[];
  parameterDefinitions: ParameterDefinitions | null;
  uiParametersByAdType: UIParametersByAdType | null;
  assetsValues: ExistingAssetsValues | null;

  campaignSummary: CampaignSummaryData | null;
  assetsTableData: AssetsTableRow[];
  assetsTableMeta: AssetsTableResponse["meta"] | null;

  getClientOptions: () => Promise<void>;
  getTeamOptions: () => Promise<void>;
  getLanguageOptions: () => Promise<void>;
  createCampaign: (campaignData: CampaignRequest) => Promise<CampaignData>;
  updateCampaign: (
    campaignData: CampaignRequest,
    id: string
  ) => Promise<CampaignData>;
  getSingleCampaign: (id: string) => Promise<void>;

  getObjectivesForFlight: () => Promise<Array<Objective>>;
  getAdCategoryForFlight: () => Promise<Array<AdCategory>>;
  createFlight: (flightData: FlightsRequest) => Promise<FlightData>;
  getFlightData: (id: string) => Promise<Array<FlightData>>;
  updateFlight: (
    flightData: FlightsUpdateRequest,
    id: string
  ) => Promise<FlightData>;
  getAssetsPageData: (campaignId: string) => Promise<AssetsPageData[]>;
  getAdCategoryData: (campaignId?: string) => Promise<void>;
  createAssets: (assetsData: AssetsStoreRequest) => Promise<void>;
  updateAssets: (assetsData: AssetsStoreRequest) => Promise<void>;

  getCampaignSummary: (campaignId: string) => Promise<CampaignSummaryData>;
  getAssetsTable: (
    campaignId: string,
    flightId?: string,
    sortColumn?: string,
    sortDirection?: string,
    page?: number
  ) => Promise<AssetsTableResponse>;
}

const useCampaignStore = create<CampaignState>((set, get) => ({
  clientOptions: [],
  teamOptions: [],
  languageOptions: [],
  objectiveOptions: [],
  adCategoryOptions: [],
  campaign: null,
  assetsPageData: [],
  adCategoryData: [],
  parameterDefinitions: null,
  uiParametersByAdType: null,
  assetsValues: null,

  campaignSummary: null,
  assetsTableData: [],
  assetsTableMeta: null,

  getClientOptions: async () => {
    try {
      const response = await apiClient.get<TeamsRespose>("/client-teams");
      const data = response.data;
      const options = data?.data?.map((team) => ({
        title: team.name,
        value: team.id,
      }));

      set({ clientOptions: options });
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getTeamOptions: async () => {
    try {
      const response = await apiClient.get<TeamsRespose>("/non-client-teams");
      const data = response.data;
      const options = data?.data?.map((team) => ({
        title: team.name,
        value: team.id,
      }));

      set({ teamOptions: options });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getLanguageOptions: async () => {
    try {
      const response = await apiClient.get<LanguageResponse>("/languages");
      const data = response.data;

      const options = data?.data?.map((language) => ({
        title: language.name,
        value: language.id,
      }));

      set({ languageOptions: options });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  createCampaign: async (campaignData: CampaignRequest) => {
    try {
      const response = await apiClient.post("/campaigns", campaignData);
      const data: CampaignData = response.data.data;
      useDashboardStore.getState().addCampaignData(data);
      set({ campaign: data });

      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  updateCampaign: async (campaignData: CampaignRequest, id: string) => {
    try {
      const response = await apiClient.put(`/campaigns/${id}`, campaignData);
      const data: CampaignData = response.data.data;
      useDashboardStore.getState().addCampaignData(data);
      set({ campaign: data });

      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getSingleCampaign: async (id: string) => {
    try {
      const response = await apiClient.get<CampaignDetailResponse>(
        `/campaigns/${id}`
      );
      const data = response.data;

      set({ campaign: data.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  //Flight Page API

  getObjectivesForFlight: async () => {
    try {
      const response = await apiClient.get<ObjectiveResponse>(
        "/list-all-objectives"
      );
      const data: Array<Objective> = response.data.data;
      set({
        objectiveOptions: data,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getAdCategoryForFlight: async () => {
    try {
      const response = await apiClient.get<AdCategoryResponse>(
        "/list-ad-categories"
      );
      const data: Array<AdCategory> = response.data.data;
      set({
        adCategoryOptions: data,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  createFlight: async (flightData: FlightsRequest) => {
    try {
      const response = await apiClient.post<FlightResponse>(
        "/flights",
        flightData
      );
      const data: FlightData = response.data.data;
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getFlightData: async (id: string) => {
    try {
      const response = await apiClient.get<FlightGetResponse>(
        `/campaigns/${id}/flights`
      );
      const data: Array<FlightData> = response.data.data;
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  updateFlight: async (flightData: FlightsUpdateRequest, id: string) => {
    try {
      const response = await apiClient.put<FlightResponse>(
        `/flights/${id}`,
        flightData
      );
      const data: FlightData = response.data.data;
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  // Assets Page API
  getAssetsPageData: async (campaignId: string) => {
    try {
      const response = await apiClient.get<AssetsPageResponse>(
        `/assets/campaign/${campaignId}`
      );
      const data: AssetsPageData[] = response.data.data;
      set({ assetsPageData: data });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getAdCategoryData: async (campaignId?: string) => {
    try {
      let query = `with=publishers,ad_types,parameter_values`;

      if (campaignId) {
        query = query + `&campaign_id=${campaignId}`;
      }

      const response = await apiClient.get<AdCategoryDataResponse>(
        `/assets/ad-categories?${query}`
      );
      const data = response.data.data;
      const parameterDefinitions = response.data.parameter_definitions;
      const uiParametersByAdType = response.data.ui_parameters_by_ad_type;

      const assetsValues = (response.data as any).existing_assets_values;

      set({
        adCategoryData: data,
        parameterDefinitions,
        uiParametersByAdType,
        assetsValues,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  createAssets: async (assetsData: AssetsStoreRequest) => {
    try {
      const response = await apiClient.post("/assets", assetsData);
      const data = response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  updateAssets: async (assetsData: AssetsStoreRequest) => {
    try {
      const response = await apiClient.put(
        `/assets/${assetsData.campaign_id}`,
        assetsData
      );
      const data = response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  //Campaign Summary API

  getCampaignSummary: async (campaignId: string) => {
    try {
      const response = await apiClient.get<CampaignSummaryResponse>(
        `/campaigns/${campaignId}/summary?include=assets`
      );
      const data = response.data.data;
      set({ campaignSummary: data });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getAssetsTable: async (
    campaignId: string,
    flightId?: string,
    sortColumn: string = "publisher",
    sortDirection: string = "asc",
    page: number = 1
  ) => {
    try {
      let url = `/campaigns/${campaignId}/assets-table?page=${page}&sort=${sortColumn}&direction=${sortDirection}`;

      if (flightId) {
        url += `&flight_id=${flightId}`;
      }

      const response = await apiClient.get<AssetsTableResponse>(url);
      const data = response.data;
      set({
        assetsTableData: data.data,
        assetsTableMeta: data.meta,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useCampaignStore;
